import { fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { logDevMessage, mapCamel } from 'utils/utils';
import select from 'store/select';
import FusionQueryError from './FusionQueryError';

const createFusionFetchClient = (baseUrl) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const apiToken = select.tokens.api(getState());
      const integrationToken = localStorage.getItem('integrationToken');

      headers.set('Authorization', apiToken);
      headers.set('X-Integration-Token', integrationToken);
      return headers;
    },
  });

const shouldNotRetry = (error) => error?.status < 500;

const camelizeResponse = (response) => {
  response.data = mapCamel(response.data);
  return response;
};

const addEndpointToMeta = (response, { endpoint, type }) => {
  if (response.meta) {
    response.meta.endpoint = endpoint;
    response.meta.type = type;
  } else {
    response.meta = { endpoint, type };
  }
  return response;
};

const runWithRetry = (baseQueryFn, args, api, extraOptions) =>
  retry(
    async (...allArgs) => {
      const result = await baseQueryFn(...allArgs);
      if (shouldNotRetry(result.error)) {
        retry.fail(result.error, result.meta);
      }
      return result;
    },
    { maxRetries: 1 }
  )(args, api, extraOptions);

const fusionFetchBase = async (args, api, extraOptions) => {
  const state = api.getState();
  const fusionUrl = select.config.apiUrl(state);
  const fusionFetchClient = createFusionFetchClient(fusionUrl);

  let result;

  try {
    result = await runWithRetry(fusionFetchClient, args, api, extraOptions);
    camelizeResponse(result);
    addEndpointToMeta(result, api);

    if (result.error) {
      const error = new FusionQueryError(result, args);
      logDevMessage(error, 'error');
      if (error.shouldReport) error.report();
      if (error.shouldRedirect) error.redirect();
    }
  } catch (fetchError) {
    result = { error: { status: 'FETCH_ERROR', originalStatus: 0, data: fetchError.message } };
  }

  return result;
};

export default fusionFetchBase;
